import React, { useRef, useState, useCallback } from "react";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import draftToHtml from "draftjs-to-html";
import { DialogContainer, Button } from "react-md";
import { Editor } from "react-draft-wysiwyg";
import crossIcon from "../../../../../assets/icons/close-cross1.png";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

export function useFAQDialog() {
  const [dialogState, setDialogState] = useState(null);
  const resolveRef = useRef(null);

  const [questionEditorState, setQuestionEditorState] = useState(() =>
    EditorState.createEmpty(),
  );
  const [answerEditorState, setAnswerEditorState] = useState(() =>
    EditorState.createEmpty(),
  );

  const htmlToEditorState = (html) => {
    const contentBlock = htmlToDraft(html || "");
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks,
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  const confirm2 = useCallback(
    ({
      title,
      message,
      viewOnly,
      questionText,
      answerText,
      buttons,
      width,
    }) => {
      return new Promise((resolve) => {
        resolveRef.current = resolve;

        if (questionText !== "NA") {
          setQuestionEditorState(htmlToEditorState(questionText || ""));
        } else {
          setQuestionEditorState(EditorState.createEmpty());
        }

        setAnswerEditorState(htmlToEditorState(answerText || ""));

        setDialogState({
          title,
          message,
          viewOnly,
          buttons,
          width,
          showQuestion: questionText !== "NA",
        });
      });
    },
    [],
  );

  const handleClose = useCallback(
    (buttonKey = null) => {
      if (resolveRef.current) {
        const questionHtml = dialogState?.showQuestion
          ? draftToHtml(convertToRaw(questionEditorState.getCurrentContent()))
          : undefined;

        const answerHtml = draftToHtml(
          convertToRaw(answerEditorState.getCurrentContent()),
        );

        resolveRef.current({
          buttonKey,
          questionText: questionHtml,
          answerText: answerHtml,
        });

        resolveRef.current = null;
      }

      setDialogState(null);
      setQuestionEditorState(EditorState.createEmpty());
      setAnswerEditorState(EditorState.createEmpty());
    },
    [dialogState, questionEditorState, answerEditorState],
  );

  return {
    confirm2,
    dialogState,
    questionEditorState,
    setQuestionEditorState,
    answerEditorState,
    setAnswerEditorState,
    handleClose,
  };
}

export function FAQDialog({
  dialogState,
  questionEditorState,
  setQuestionEditorState,
  answerEditorState,
  setAnswerEditorState,
  handleClose,
}) {
  if (!dialogState) return null;

  const getButtonStyle = (color) => {
    const baseStyle = {
      cursor: "pointer",
      marginLeft: "10px",
      minWidth: "125px",
      textTransform: "none",
      fontSize: "medium",
      borderRadius: "5px",
      color: "#FFFFFF",
    };

    switch (color) {
      case "zyOrange":
        return { ...baseStyle, backgroundColor: "#F77F11" };
      case "zyGreen":
        return { ...baseStyle, backgroundColor: "#04C9D3" };
      case "zyGrey":
        return { ...baseStyle, backgroundColor: "#EBEBEB", color: "#333333" };
      default:
        return baseStyle;
    }
  };

  return (
    <DialogContainer
      id="reusable-faq-dialog"
      visible={true}
      focusOnMount={false}
      onHide={() => {
        if (dialogState.viewOnly === true) {
          handleClose("1");
        }
      }}
      autosizeContent
      height="auto"
      style={{ textAlign: "left" }}
      dialogStyle={{ borderRadius: "10px", position: "relative" }}
      width={dialogState.width || "600px"}
    >
      <div
        style={{
          padding: "15px 15px 8px 15px",
          color: "black",
          fontFamily: "Montserrat",
          fontSize: "14px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <h3>{dialogState.title}</h3>
          </div>
          {dialogState.buttons?.["1"] === "close" && (
            <div>
              <Button
                plain
                style={{
                  backgroundColor: "transparent",
                  boxShadow: "none",
                  minWidth: "auto",
                  padding: 0,
                }}
                onClick={() => handleClose("1")}
              >
                <img src={crossIcon} alt="close" height="25px" />
              </Button>
            </div>
          )}
        </div>

        {dialogState.message && dialogState.showQuestion && (
          <div>{dialogState.message}</div>
        )}

        {dialogState.showQuestion && (
          <div
            id="question_section"
            style={{ marginTop: "16px", borderBottom: "1px solid #C2C2C2" }}
          >
            <div style={{ fontWeight: "700" }}>Question</div>
            <Editor
              id="question_section"
              readOnly={dialogState.viewOnly === true}
              editorState={questionEditorState}
              wrapperClassName="editor-wrapper"
              editorClassName="editor-main"
              onEditorStateChange={setQuestionEditorState}
              stripPastedStyles={true}
            />
          </div>
        )}

        <div
          id="answer_section"
          style={{ marginTop: "16px", borderBottom: "1px solid #C2C2C2" }}
        >
          {dialogState.showQuestion && (
            <div style={{ fontWeight: "700" }}>Answer</div>
          )}
          <Editor
            id="answer_section"
            readOnly={dialogState.viewOnly === true}
            editorState={answerEditorState}
            wrapperClassName="editor-wrapper"
            editorClassName="editor-main"
            onEditorStateChange={setAnswerEditorState}
            stripPastedStyles={true}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: "30px",
          }}
        >
          <div style={{ textAlign: "right" }}>
            {Object.entries(dialogState.buttons).map(([key, btn]) => {
              if (key === "1") return null;
              const label = typeof btn === "string" ? btn : btn.label;
              const color = typeof btn === "object" ? btn.color : "zyGrey";
              return (
                <Button
                  key={key}
                  raised
                  style={getButtonStyle(color)}
                  onClick={() => handleClose(key)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>
    </DialogContainer>
  );
}
